import { alpha, createTheme, darken } from '@mui/material'

// Design System Colors
const designSystem = {
  colors: {
    dark: {
      background: {
        primary: '#0A0A0A',
        secondary: '#1A1A1A',
        card: '#1C1C1E',
        cardHover: '#252527',
      },
      text: {
        primary: '#FFFFFF',
        secondary: '#A0A0A0',
        muted: '#6B6B6B',
      },
      border: {
        default: '#2A2A2A',
        subtle: '#1F1F1F',
      },
    },
    light: {
      background: {
        primary: '#FAFAFA',
        secondary: '#F5F5F7',
        card: '#FFFFFF',
        cardHover: '#F8F8F8',
      },
      text: {
        primary: '#1A1A1A',
        secondary: '#6B6B6B',
        muted: '#9E9E9E',
      },
      border: {
        default: '#E5E5E7',
        subtle: '#F0F0F0',
      },
    },
    accent: {
      primary: '#FF6B35',
      primaryHover: '#FF8554',
      purple: '#8B5CF6',
      green: '#10B981',
      orange: '#F97316',
    },
  },
  typography: {
    fontFamily: {
      primary: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
      mono: "'SF Mono', 'Monaco', 'Inconsolata', monospace",
    },
    fontSize: {
      xs: '12px',
      sm: '14px',
      base: '16px',
      lg: '18px',
      xl: '20px',
      '2xl': '24px',
      '3xl': '30px',
      '4xl': '48px',
    },
    fontWeight: {
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
  },
  borderRadius: {
    sm: '6px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    '2xl': '20px',
    full: '9999px',
  },
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '20px',
    '2xl': '24px',
    '3xl': '32px',
    '4xl': '40px',
    '5xl': '48px',
    '6xl': '64px',
  },
}

export const logInBackgroundImage = `radial-gradient(circle at 50% 14em, ${designSystem.colors.accent.primary} 0%, ${designSystem.colors.dark.background.primary} 70%, ${designSystem.colors.dark.background.primary} 100%)`

const darkPalette = {
  background: {
    default: designSystem.colors.dark.background.primary,
    paper: designSystem.colors.dark.background.card,
  },
  primary: {
    main: designSystem.colors.accent.primary,
    light: designSystem.colors.accent.primaryHover,
    dark: darken(designSystem.colors.accent.primary, 0.2),
  },
  secondary: {
    main: designSystem.colors.accent.purple,
  },
  error: {
    main: '#E84142',
  },
  warning: {
    main: designSystem.colors.accent.orange,
  },
  info: {
    main: '#61DFFF',
  },
  success: {
    main: designSystem.colors.accent.green,
  },
  text: {
    primary: designSystem.colors.dark.text.primary,
    secondary: designSystem.colors.dark.text.secondary,
    disabled: designSystem.colors.dark.text.muted,
  },
  divider: designSystem.colors.dark.border.default,
  mode: 'dark',
}

const lightPalette = {
  background: {
    default: designSystem.colors.light.background.primary,
    paper: designSystem.colors.light.background.card,
  },
  primary: {
    main: designSystem.colors.accent.primary,
    light: designSystem.colors.accent.primaryHover,
    dark: darken(designSystem.colors.accent.primary, 0.2),
  },
  secondary: {
    main: designSystem.colors.accent.purple,
  },
  error: {
    main: '#E84142',
  },
  warning: {
    main: designSystem.colors.accent.orange,
  },
  info: {
    main: '#61DFFF',
  },
  success: {
    main: designSystem.colors.accent.green,
  },
  text: {
    primary: designSystem.colors.light.text.primary,
    secondary: designSystem.colors.light.text.secondary,
    disabled: designSystem.colors.light.text.muted,
  },
  divider: designSystem.colors.light.border.default,
  mode: 'light',
}

const componentsOverrides = (theme) => {
  const isDark = theme.palette.mode === 'dark'
  const colors = isDark ? designSystem.colors.dark : designSystem.colors.light

  return {
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(colors.background.primary, isDark ? 0.8 : 0.5),
          backdropFilter: 'blur(2px)',
          '&.MuiBackdrop-invisible': {
            backgroundColor: 'transparent',
            backdropFilter: 'blur(2px)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: designSystem.borderRadius.lg,
          textTransform: 'none',
          fontWeight: designSystem.typography.fontWeight.semibold,
          fontSize: designSystem.typography.fontSize.base,
          padding: '12px 32px',
          transition: 'all 0.2s ease',
          '&.MuiButton-containedPrimary': {
            backgroundColor: designSystem.colors.accent.primary,
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: designSystem.colors.accent.primaryHover,
            },
          },
          '&.MuiButton-outlined': {
            backgroundColor: colors.background.card,
            color: colors.text.primary,
            borderColor: colors.border.default,
            padding: '10px 20px',
            fontSize: designSystem.typography.fontSize.sm,
            fontWeight: designSystem.typography.fontWeight.medium,
            borderRadius: designSystem.borderRadius.md,
            '&:hover': {
              borderColor: colors.border.default,
              backgroundColor: colors.background.cardHover,
            },
          },
          '&.MuiButton-text': {
            color: colors.text.secondary,
            '&:hover': {
              backgroundColor: alpha(colors.background.card, isDark ? 0.5 : 0.1),
            },
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.card,
          borderRadius: designSystem.borderRadius['2xl'],
          padding: designSystem.spacing['2xl'],
          border: `1px solid ${colors.border.default}`,
          transition: 'all 0.2s ease',
          boxShadow: isDark ? 'none' : '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          '&:hover': {
            borderColor: isDark ? '#3A3A3A' : colors.border.default,
            boxShadow: isDark ? 'none' : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.card,
          borderRadius: designSystem.borderRadius['2xl'],
          border: `1px solid ${colors.border.default}`,
          boxShadow: isDark ? 'none' : '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiFormControl: {
      defaultProps: {
        margin: 'dense',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: designSystem.borderRadius.md,
          backgroundColor: colors.background.card,
          '& fieldset': {
            borderColor: colors.border.default,
          },
          '&:hover fieldset': {
            borderColor: colors.border.default,
          },
          '&.Mui-focused fieldset': {
            borderColor: designSystem.colors.accent.primary,
          },
        },
        input: {
          padding: `${designSystem.spacing.md} ${designSystem.spacing.lg}`,
          color: colors.text.primary,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: colors.text.secondary,
          '&.Mui-focused': {
            color: designSystem.colors.accent.primary,
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          padding: '10px 20px',
          borderRadius: designSystem.borderRadius.md,
          fontSize: designSystem.typography.fontSize.sm,
          fontWeight: designSystem.typography.fontWeight.medium,
          color: colors.text.muted,
          textTransform: 'none',
          minHeight: 'auto',
          transition: 'all 0.2s ease',
          '&.Mui-selected': {
            color: designSystem.colors.accent.purple,
            backgroundColor: isDark ? '#2A2A5C' : alpha(designSystem.colors.accent.purple, 0.1),
          },
          '&:hover': {
            color: colors.text.secondary,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.card,
          borderRadius: designSystem.borderRadius.lg,
          padding: designSystem.spacing.xs,
          minHeight: 'auto',
        },
        indicator: {
          display: 'none',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${colors.border.default}`,
          '&:last-child td': {
            border: 0,
          },
          '&:hover': {
            backgroundColor: colors.background.cardHover,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: `${designSystem.spacing.xl} ${designSystem.spacing['2xl']}`,
          fontSize: designSystem.typography.fontSize.base,
          color: colors.text.primary,
          borderBottom: `1px solid ${colors.border.default}`,
          '&.MuiTableCell-head': {
            fontSize: designSystem.typography.fontSize.sm,
            color: colors.text.muted,
            fontWeight: designSystem.typography.fontWeight.medium,
            padding: `${designSystem.spacing.xl} ${designSystem.spacing['2xl']}`,
            borderBottom: `1px solid ${colors.border.default}`,
          },
          '&.MuiTableCell-sizeSmall': {
            padding: `${designSystem.spacing.lg} ${designSystem.spacing['2xl']}`,
          },
          '&.MuiTableCell-paddingNone': {
            padding: designSystem.spacing.xs,
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.card,
          borderRadius: designSystem.borderRadius['2xl'],
          border: `1px solid ${colors.border.default}`,
          boxShadow: isDark ? 'none' : '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: designSystem.borderRadius.md,
          padding: '4px 8px',
          fontSize: designSystem.typography.fontSize.xs,
          fontWeight: designSystem.typography.fontWeight.semibold,
          height: 'auto',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.primary,
          borderBottom: `1px solid ${colors.border.subtle}`,
          boxShadow: isDark ? 'none' : '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
        },
      },
    },
    RaAppBar: {
      styleOverrides: {
        root: {
          color: theme.palette.text.primary,
          backgroundColor: colors.background.primary,
          borderBottom: `1px solid ${colors.border.subtle}`,
          boxShadow: isDark ? 'none' : '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          '& .RaAppBar-toolbar': {
            backgroundColor: colors.background.primary,
            color: colors.text.primary,
            padding: '0 48px',
          },
        },
      },
    },
    RaSidebar: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.secondary,
          borderRight: `1px solid ${colors.border.subtle}`,
        },
      },
    },
    RaMenuItemLink: {
      styleOverrides: {
        root: {
          padding: designSystem.spacing.md,
          marginRight: designSystem.spacing.sm,
          marginLeft: designSystem.spacing.sm,
          borderRadius: designSystem.borderRadius.md,
          color: colors.text.secondary,
          fontSize: designSystem.typography.fontSize.base,
          fontWeight: designSystem.typography.fontWeight.medium,
          transition: 'all 0.2s ease',
          '&:hover': {
            borderRadius: designSystem.borderRadius.md,
            backgroundColor: colors.background.card,
            color: colors.text.primary,
          },
          '&.RaMenuItemLink-active': {
            borderRadius: designSystem.borderRadius.md,
            backgroundColor: colors.background.card,
            color: colors.text.primary,
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: '2px',
              backgroundColor: designSystem.colors.accent.primary,
            },
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.background.secondary,
          borderRight: `1px solid ${colors.border.subtle}`,
        },
      },
    },
  }
}

const createCustomTheme = (palette) => {
  const themeOptions = {
    palette,
    shape: {
      borderRadius: parseInt(designSystem.borderRadius.md),
    },
    sidebar: {
      width: 250,
      closedWidth: 60,
    },
    spacing: 8,
    typography: {
      fontFamily: designSystem.typography.fontFamily.primary,
      h1: {
        fontWeight: designSystem.typography.fontWeight.bold,
        fontSize: designSystem.typography.fontSize['4xl'],
        lineHeight: 1.2,
      },
      h2: {
        fontWeight: designSystem.typography.fontWeight.bold,
        fontSize: designSystem.typography.fontSize['3xl'],
        lineHeight: 1.2,
      },
      h3: {
        fontWeight: designSystem.typography.fontWeight.semibold,
        fontSize: designSystem.typography.fontSize['2xl'],
        lineHeight: 1.2,
      },
      h4: {
        fontWeight: designSystem.typography.fontWeight.semibold,
        fontSize: designSystem.typography.fontSize.xl,
        lineHeight: 1.5,
      },
      h5: {
        fontWeight: designSystem.typography.fontWeight.medium,
        fontSize: designSystem.typography.fontSize.lg,
        lineHeight: 1.5,
      },
      h6: {
        fontWeight: designSystem.typography.fontWeight.medium,
        fontSize: designSystem.typography.fontSize.base,
        lineHeight: 1.5,
      },
      body1: {
        fontSize: designSystem.typography.fontSize.base,
        lineHeight: 1.5,
      },
      body2: {
        fontSize: designSystem.typography.fontSize.sm,
        lineHeight: 1.5,
      },
      button: {
        textTransform: 'none',
        fontWeight: designSystem.typography.fontWeight.semibold,
        fontSize: designSystem.typography.fontSize.base,
      },
      caption: {
        fontSize: designSystem.typography.fontSize.xs,
        lineHeight: 1.5,
      },
    },
    shadows: [
      'none',
      '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
      '0 0 20px rgba(139, 92, 246, 0.3)',
      '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
      '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    ],
    breakpoints: {
      values: {
        xs: 0,
        sm: 640,
        md: 768,
        lg: 1024,
        xl: 1440,
      },
    },
  }
  const theme = createTheme(themeOptions)
  theme.components = componentsOverrides(theme)
  return theme
}

export const customLightTheme = createCustomTheme(lightPalette)
export const customDarkTheme = createCustomTheme(darkPalette)
